<script>
import FdxScrollFade from '@common/components/FdxScrollFade.vue';
import { useMixpanel } from '@common/composables/useMixpanel.js';
import { parseObject } from '@common/utilities/queryTypes.js';
import { FdxDropdown } from '@foodics/ui-common';
import {
  camelCase,
  clone,
  cloneDeep,
  find,
  flatten,
  forOwn,
  includes,
  isEmpty,
  isEqual,
  isNull,
  isUndefined,
  map,
  omit,
  omitBy,
  pick,
  pull,
} from 'lodash-es';
import NProgress from 'nprogress';
import qs from 'qs';

import { Bus } from '../bus';
import { useHttp } from '../composables/useHttp.js';
import { roundDownToNearestThousand } from '../utils/number';
import { isObjectEmpty } from '../utils/object';
import FdxIntersectionTrigger from './FdxIntersectionTrigger.vue';

export default {
  beforeUnmount() {
    Bus.$off('select-input:choose-option');
    this.requestController.abort();
  },
  components: {
    FdxDropdown,
    FdxIntersectionTrigger,
    FdxScrollFade,
  },
  computed: {
    allItemsSelected() {
      return (
        this.selectedItemsIds.length > 0 &&
        this.selectedItemsIds.length ===
          this.items.filter((item) => !this.excludeFromSelectionCallback(item)).length
      );
    },
    canShowPaginationInfo() {
      return this.responseMeta && this.responseMeta.to && this.responseMeta.total;
    },
    displayItems() {
      // items that are filtered and sorted and ready to be displayed
      return this.itemsFilter(this.items, this.filters);
    },
    isBulkActionsDisabled() {
      return typeof this.disableBulkActions === 'function'
        ? this.disableBulkActions(this.items)
        : this.disableBulkActions;
    },

    isCustomerPortal() {
      return this.$root.mainStore.isCustomerPortal;
    },
    shouldShowFiltersChips() {
      return (
        !this.showTabs && !this.initializing && this.activeFiltersCount() && !this.hideFilterChips
      );
    },
    showTabs() {
      const query = qs.parse(this.$route.query);
      return this.tabs.length > 0 && (!query.filter || Boolean(query.showTabs));
    },
  },
  data() {
    return {
      currentCursor: '',
      currentPage: 1,
      currentTab: null,
      filters: {},
      filtersModalForm: {},
      filtersModalOpened: false,
      focusOnError: false,
      hasFiltersBeenSelected: false,
      hasPagination: true,
      initializing: true,
      isCursor: false,
      isFetching: false,
      items: [],
      querySort: null,
      requestController: {},
      responseDetails: null,
      responseMeta: null,
      searchQuery: '',
      // for caching all selected items details
      selectedItems: [],
      selectedItemsIds: [],
      selectedOptionsFromSelectInput: {},
      sort: this.defaultSort,
      supplementFilterChips: {},
    };
  },
  emits: [
    'items-selected',
    'updated-filters',
    'fetching',
    'applied-filters',
    'update:total',
    'fetched',
    'row-clicked-with-ctrl',
    'row-clicked',
  ],
  methods: {
    activeFiltersCount() {
      const filters = omitBy(this.filters, (v) => v === '');

      return Object.keys(filters).length;
    },
    async applyFilters(showTabs = false) {
      const filters = await this.externalFiltersTransform(clone(this.filters));

      this.resetPagination();
      //  this is used for filter-chips component to get the selected Labels
      // use flatten for multi-select values
      this.selectedOptionsFromSelectInput = isEmpty(filters)
        ? {}
        : pick(this.selectedOptionsFromSelectInput, flatten(Object.values(filters)));

      const queryParams = {
        filter: filters,
        filterChips: { ...this.selectedOptionsFromSelectInput },
        page: this.currentPage,
        showTabs: showTabs ? true : null,
      };

      if (this.isCustomerPortal && !showTabs) {
        this.mixpanelTrackFilters(filters);
      }
      this.updateURL(queryParams);
    },
    camelCase,
    changeCurrentTab() {
      const current = find(this.tabs, (tab) => {
        return isEqual(tab.filters, this.filters);
      });

      this.currentTab = current ? current.filters : null;
    },
    changeRouteForPagination(direction) {
      const directionCursorMap = {
        next: this.responseMeta?.next_cursor,
        prev: this.responseMeta?.prev_cursor,
      };

      this.updateURL({
        page: this.isCursor ? directionCursorMap[direction] : this.currentPage,
      });

      this.$refs.tableContent.scrollTop = 0;
    },
    clearAllFilters() {
      if (this.showTabs) {
        return;
      }
      this.filtersModalForm = this.clearFiltersCallback(this.filters);
      this.submitFiltersModalForm();
    },
    clearFilter(keys) {
      this.filtersModalForm = omit(this.filtersModalForm, keys);
      this.submitFiltersModalForm();
    },
    closeFiltersModal() {
      this.filtersModalOpened = false;
      // Reset the filters modal form to the current filters as sometimes
      // filtersModalForm updated but not applied because of the validation
      this.filtersModalForm = cloneDeep(this.filters);
      this.focusOnError = false;
    },
    fetchItems() {
      NProgress.start();
      this.isFetching = true;

      this.requestController = new AbortController();
      const signal = this.requestController.signal;
      const filters = { ...this.defaultFilters, ...this.filters };

      this.$emit('fetching', { payload: true });
      this.$emit('applied-filters', this.filters);

      const requestBody = {
        filter: filters,
        include: this.requestIncludes,
        page: this.currentCursor ? this.currentCursor : this.currentPage,
        sort: this.querySort,
        ...(this.searchQuery && { search: this.searchQuery }),
        ...this.supplementRequestData,
      };
      const request =
        this.requestType === 'post'
          ? this.http.post(this.requestUrl, requestBody)
          : this.http.get(`${this.requestUrl}?${qs.stringify(requestBody)}`, { signal });

      Promise.resolve(request)
        .then((response) => {
          this.$emit('fetching', { payload: false });
          this.responseMeta = response.data.meta || null;
          this.responseDetails = response?.data;

          this.isCursor = Boolean(response.data.is_cursor);
          const totalCount = response.data?.meta?.total;
          if (this.isCursor) {
            this.$emit('update:total', response.data.data.length ? Infinity : 0);
          }
          if (totalCount !== undefined && totalCount !== null) {
            this.$emit('update:total', totalCount);
          }
          if (!response.data.meta && response.data.data) {
            this.$emit('update:total', response.data.data.length);
          }
          if (this.infiniteScroll) {
            this.items.push(
              ...(this.dataFilter ? this.dataFilter(response.data.data) : response.data.data)
            );
          } else {
            this.items = this.dataFilter ? this.dataFilter(response.data.data) : response.data.data;

            this.currentPage = response.data.meta ? parseInt(response.data.meta.current_page) : 1;
          }

          this.$emit('fetched', { data: cloneDeep(this.items) });
          this.hasPagination = Boolean(response.data.meta);
        })
        .catch((error) => {
          if (this.errorCallback) {
            this.errorCallback(error);
          }
        })
        .finally(() => {
          this.isFetching = false;
          this.initializing = false;
          NProgress.done();
        });
    },
    handleRowClick(item, withCtrl = false) {
      if (!this.isRowClickable || window.getSelection().toString()) {
        return;
      }

      this.$root.mainStore.cachePrevFullPath();

      if (withCtrl) {
        this.$emit('row-clicked-with-ctrl', item);
      } else {
        this.$emit('row-clicked', item);
      }
    },
    init() {
      const query = parseObject(qs.parse(this.$route.query));
      this.filters = cloneDeep(query.filter || this.hiddenFilters);
      this.filtersModalForm = cloneDeep(this.filters);
      this.selectedOptionsFromSelectInput = query.filterChips ?? {};
      this.searchQuery = query.search ?? '';
      this.supplementFilterChips = query.supplementFilterChips ?? {};

      if (query.page && parseInt(query.page)) {
        this.currentPage = parseInt(query.page);
      } else if (query.page) {
        this.currentCursor = query.page;
      } else {
        this.currentPage = 1;
      }

      if (query.sort) {
        this.sort = {
          by: query.sort,
          direction: query.direction,
        };
      } else {
        this.sort = { ...this.defaultSort };
      }

      this.querySort = this.sort.direction + this.sort.by;

      this.selectedItemsIds = [];
      this.selectedItems = [];
      this.changeCurrentTab();

      if (this.infiniteScroll) {
        this.items = [];
      }

      if (this.filtersRequired && !this.hasFiltersBeenSelected) {
        this.filtersModalOpened = true;
        this.initializing = false;
      } else {
        this.fetchItems();
      }
    },
    isCurrentTab(tab) {
      return this.currentTab === tab?.filters;
    },
    itemIsSelected(item) {
      return includes(this.selectedItemsIds, item.id);
    },
    mixpanelTrackFilters(filters) {
      const dimensions = Object.keys(filters);
      this.mixpanelTrackEvent('CP Filter', {
        dimensions,
      });
    },
    mixpanelTrackSoftFilters(tab) {
      const englishSoftFilterName = this.$t(tab.title, 'en');
      this.mixpanelTrackEvent('CP Soft Filter', {
        softFilter: englishSoftFilterName,
      });
    },
    nextPage() {
      if (this.infiniteScroll && this.responseMeta?.last_page >= this.currentPage) {
        this.currentPage++;
        this.fetchItems();
      } else if (!this.infiniteScroll) {
        if (!this.isCursor) {
          this.currentPage += 1;
        }
        this.changeRouteForPagination('next');
      }
    },
    overwriteFilters(data) {
      return (this.filtersModalForm = { ...data });
    },
    previousPage() {
      if (!this.isCursor) {
        this.currentPage = this.currentPage - 1;
      }

      this.changeRouteForPagination('prev');
    },
    refreshItems() {
      this.selectedItemsIds = [];
      this.selectedItems = [];

      this.fetchItems();
    },
    resetPagination() {
      this.currentPage = 1;
      this.currentCursor = '';
    },
    roundDownToNearestThousand,
    searchClick(query) {
      if (this.searchQuery === query) {
        return;
      }

      this.searchQuery = query;
      this.resetPagination();

      this.updateURL({
        page: this.currentPage,
        search: query,
      });
    },
    submitButtonClicked() {
      if (!this.isValidFilters(this.filtersModalForm)) {
        this.focusOnError = true;
        this.$refs.filterInfoRef.scrollIntoView({
          behavior: 'smooth',
        });
        return;
      }
      this.filtersModalForm = omitBy(
        this.filtersModalForm,
        (v) => isNull(v) || isUndefined(v) || v === ''
      );
      if (qs.stringify(this.filtersModalForm) === qs.stringify(this.filters)) {
        this.closeFiltersModal();
        return;
      }
      this.submitFiltersModalForm();
    },
    submitFiltersModalForm() {
      this.hasFiltersBeenSelected = true;
      this.filters = cloneDeep(this.filtersCallback(cloneDeep(this.filtersModalForm)));
      this.closeFiltersModal();
      this.applyFilters();
    },
    switchTabs(tab, isForced = false) {
      if (tab.filters === null) {
        this.filters = {};
      }
      if (this.isCurrentTab(tab) && tab.filters !== null) {
        return;
      }
      if (!isForced) {
        this.filters = {};
      }

      forOwn(tab.filters, (value, key) => {
        this.filters[key] = value;
      });
      if (this.isCustomerPortal) {
        this.mixpanelTrackSoftFilters(tab);
      }
      this.filtersModalForm = clone(this.filtersCallback(this.filters));
      this.applyFilters(true);
    },
    toggleAllItemsSelection() {
      if (this.allItemsSelected) {
        this.selectedItemsIds = [];
        this.selectedItems = [];
      } else {
        const finalSelectedItems = this.items.filter(
          (item) => !this.excludeFromSelectionCallback(item)
        );
        this.selectedItemsIds = map(finalSelectedItems, 'id');
        this.selectedItems = clone(finalSelectedItems);
      }
    },
    toggleItemSelection(item) {
      if (this.itemIsSelected(item)) {
        this.selectedItems = this.selectedItems.filter(
          (selectedItem) => selectedItem.id !== item.id
        );
        return (this.selectedItemsIds = pull(clone(this.selectedItemsIds), item.id));
      }

      this.selectedItems = [...this.selectedItems, item];
      this.selectedItemsIds = [...this.selectedItemsIds, item.id];
    },
    updateSortBy(column) {
      if (this.sort.by === column) {
        this.sort.direction = this.sort.direction === '' ? '-' : '';
        if (!this.sort.direction) {
          this.sort.by = '';
        }
      } else {
        this.sort = { by: column, direction: '' };
      }

      this.updateURL({
        direction: this.sort.direction,
        sort: this.sort.by,
      });
    },
    updateURL(queryParams = {}) {
      const currentQueryParams = qs.parse(this.$route.query) ?? {};
      const finalQueryParams = qs.stringify(
        {
          ...currentQueryParams,
          ...queryParams,
        },
        { skipNulls: true }
      );
      this.$router.push(`${this.$route.path}?${finalQueryParams}${this.$route.hash || ''}`);
    },
  },
  mounted() {
    if (this.title.length && !this.isCustomerPortal) {
      document.title = `${this.$t(this.title)} - ${this.$t('misc.foodics')}`;
    }

    if (!isObjectEmpty(this.filters)) {
      this.hasFiltersBeenSelected = true;
    }

    this.init();

    Bus.$on('select-input:choose-option', (data) => {
      this.selectedOptionsFromSelectInput = {
        ...this.selectedOptionsFromSelectInput,
        ...data,
      };
    });
  },
  props: {
    breadcrumbHref: {
      default: '',
      type: String,
    },
    cardsColumns: {
      default: '3',
      type: String,
    },
    clearFiltersCallback: {
      default() {
        return {};
      },
      // callback that can be used where there is a required filter, filters will be set to whatever this CB returns;
      type: Function,
    },
    columns: { default: () => [], type: Array },
    dataFilter: { default: null, type: Function },
    dataTestIdPrefix: {
      default: '',
      type: String,
    },
    // ::TODO Refactor the all types filters
    /*
     * these filters are sent with the fetching request as long as they are not overrided from the parent component
     * and not passed to the url as query params and not sent with the export action
     */
    defaultFilters: { default: () => ({}), type: Object },
    defaultSort: {
      default() {
        return { by: 'created_at', direction: '-' };
      },
      type: Object,
    },
    /*
     * disableBulkActions is used to disable the bulk actions
     * if it's a function, it will be called with the items as argument
     */
    disableBulkActions: {
      default: true,
      type: [Boolean, Function],
    },
    enableDeletedAtFilter: {
      default: true,
      type: Boolean,
    },
    errorCallback: {
      default: null,
      // a callback that is called when a fetching error occur, the error object is passd to this callback.
      type: Function,
    },
    excludeFromSelectionCallback: {
      default() {
        return false;
      },
      type: Function,
    },
    externalFiltersTransform: {
      default(filters) {
        return filters;
      },
      // an async callback that can be used to transfor filters, ex: selecting branches by tags you'd need to transfor branch_tag_id to branch_id
      type: Function,
    },
    filtersCallback: {
      default: (filters) => filters,
      type: Function,
    },
    filtersRequired: {
      // when true, this component will NOT load the items initially, rather it will open the filters modal to allow user to filter first then fetch, used in reports where huge data is expected, ex: inventory history report
      type: Boolean,
    },
    fixedFirstCol: {
      // a flag to keep table first row fixed to the left
      type: Boolean,
    },
    fixedHeader: {
      default: true,
      // a flag to keep table header fixed at the top
      type: Boolean,
    },
    forceBreadcrumbHref: {
      default: true,
      type: Boolean,
    },
    hasFilters: {
      default: true,
      type: Boolean,
    },
    hasSearch: {
      type: Boolean,
    },
    headerClasses: {
      default: 'py-4',
      type: String,
    },
    helpCenterPage: {
      default: '',
      type: String,
    },
    /*
     * these filters are used to replace the query params filters if no query params filters are passed
     * and you should pass it to the export-import-actions component to be sent to the export action
     */
    hiddenFilters: {
      default() {
        return {};
      },
      type: Object,
    },
    hideFilterChips: {
      type: Boolean,
    },
    ignoreRouteChanges: {
      default() {
        return false;
      },
      type: Function,
    },
    infiniteScroll: {
      type: Boolean,
    },
    isFilterInHeader: {
      default: true,
      type: Boolean,
    },
    isRowClickable: {
      default: true,
      type: Boolean,
    },
    isValidFilters: {
      default: () => true,
      type: Function,
    },
    itemsFilter: {
      default(items) {
        return items;
      },
      type: Function,
    },
    keysLookup: {
      default() {
        return {};
      },
      type: Object,
    },
    listingStyle: {
      default: 'table',
      type: String,
    },
    noRecordsText: { default: 'No data to display!', type: String },
    noWrap: {
      type: Boolean,
    },
    paddingClasses: {
      default: 'px-6',
      type: String,
    },
    perPage: {
      default: 50,
      // when this is passed as -1, it will signal that per page is unknown (ex: inventory level report)
      type: Number,
    },
    relatedFilterChipKeys: {
      default: () => ({}),
      type: Object,
    },
    requestIncludes: { default: () => [], type: Array },
    requestType: { default: 'get', type: String },
    requestUrl: { required: true, type: String },
    searchFields: {
      default: () => [],
      type: Array,
    },
    searchHint: {
      default: '',
      type: String,
    },
    showHeader: {
      default: true,
      type: Boolean,
    },
    showInfoText: {
      type: Boolean,
    },
    showRefreshButton: {
      default: true,
      type: Boolean,
    },
    supplementRequestData: {
      default() {
        return {};
      },
      type: Object,
    },
    tableClasses: {
      default: 'card',
      type: String,
    },
    tableTitle: {
      default: '',
      type: String,
    },
    tabs: { default: () => [], type: Array },
    title: {
      default: '',
      type: String,
    },
  },
  setup() {
    const { mixpanelTrackEvent } = useMixpanel();

    const http = useHttp();

    return {
      http,
      mixpanelTrackEvent,
    };
  },
  watch: {
    $route() {
      if (this.ignoreRouteChanges()) {
        return;
      }
      this.init();
    },
    filtersModalForm: {
      deep: true,
      handler(value) {
        this.$emit('updated-filters', value);
      },
    },
    selectedItemsIds(val) {
      this.$emit('items-selected', {
        itemsIds: clone(val),
        selectedItems: clone(this.selectedItems),
      });
    },
  },
};
</script>

<template>
  <div
    ref="tableContent"
    class="screen-listing mx-auto"
    :class="[
      {
        'fixed-table': fixedHeader,
        'fixed-first-col': fixedFirstCol,
        'has-bulk-actions': !isBulkActionsDisabled,
      },
      paddingClasses,
    ]"
  >
    <decorated-modal
      v-if="filtersModalOpened"
      close-button
      :title="$t('button_label.filter')"
      :width-class="'md:max-w-[600px]'"
      body-container-class="filter-modal-body"
      drawer
      @close="closeFiltersModal"
    >
      <template #body>
        <div
          v-if="showInfoText"
          ref="filterInfoRef"
          :class="[
            'mb-4 rounded border border-blue-100 bg-blue-100 px-4 py-3',
            {
              '!border-primary': focusOnError,
            },
          ]"
        >
          <slot name="filtersModalInfo" />
        </div>
        <slot name="filtersModalContent" :filters="filtersModalForm" />
      </template>

      <template #footer>
        <button
          v-if="!showTabs"
          class="me-auto text-gray-700 hover:underline"
          :data-testid="camelCase(`${dataTestIdPrefix}ClearButton`)"
          @click="clearAllFilters"
        >
          {{ $t('button_label.clear') }}
        </button>
      </template>

      <template #footerRight>
        <button
          :data-testid="camelCase(`${dataTestIdPrefix}ApplyButton`)"
          class="btn btn-primary ms-2"
          @click="submitButtonClicked"
        >
          {{ $t('button_label.apply') }}
        </button>
      </template>
    </decorated-modal>

    <div v-if="showHeader" id="table_header" data-testid="tableHeader" class="flex items-center">
      <div class="w-full">
        <div>
          <div>
            <breadcrumb
              v-if="breadcrumbHref"
              :title="$t('button_label.back')"
              :href="breadcrumbHref"
              :force-href="forceBreadcrumbHref"
            />
            <div :class="headerClasses">
              <div class="flex flex-row flex-wrap items-start justify-between gap-5">
                <div>
                  <div class="flex gap-x-5">
                    <h1 class="text-title-1 text-gray-1000 md:text-3xl">
                      {{ $t(title) }}
                    </h1>
                    <search
                      v-if="hasSearch"
                      :search-query
                      :search-hint
                      :search-fields
                      @on-search="searchClick"
                    />
                  </div>
                  <help-center-link v-if="helpCenterPage" :page="helpCenterPage" class="mt-2" />
                </div>

                <div
                  :class="[
                    'flex justify-end',
                    {
                      'w-auto md:w-full xl:w-auto': hasSearch,
                    },
                  ]"
                >
                  <slot name="tableHeaderActions" />
                </div>
              </div>

              <div v-if="$slots.tableHeaderNote" class="mt-4 flex">
                <slot name="tableHeaderNote" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <filters-chips
      v-if="shouldShowFiltersChips"
      :filters
      :keys-lookup
      :related-filter-chip-keys
      :supplement-filter-chips
      :selected-options-from-select-input
      @clear-filter="clearFilter"
      @clear-all-filters="clearAllFilters"
    />

    <div v-if="initializing">
      <screen-loader />
    </div>
    <div v-if="!initializing">
      <div v-if="listingStyle === 'table' && ($slots.subheader || $slots.subheader)">
        <slot name="subheader" :items />
      </div>

      <div v-if="listingStyle === 'table'" :class="`${tableClasses} space-y-4`">
        <div
          v-if="showTabs || showRefreshButton || hasFilters"
          class="card-header align-center flex w-full items-end gap-3"
          :class="[showTabs ? 'justify-between' : 'justify-end']"
        >
          <div v-if="showTabs" class="flex-grow-1 overflow-x-auto">
            <fdx-scroll-fade>
              <button
                v-for="(tab, i) in tabs"
                :key="i"
                :data-text="$t(tab.title)"
                :data-testid="camelCase(`listingTab-${tab.title}`)"
                class="flex flex-shrink-0 whitespace-nowrap border-b-2 border-transparent px-3 !pb-2"
                :class="{
                  '!border-primary font-bold text-gray-1000': isCurrentTab(tab),
                }"
                @click.stop.prevent="switchTabs(tab)"
              >
                {{ $t(tab.title) }}
              </button>
            </fdx-scroll-fade>
          </div>

          <div
            v-else-if="tableTitle"
            class="tabs-container flex w-full overflow-x-auto text-2xl font-normal text-gray-700 md:overflow-hidden"
          >
            {{ $t(tableTitle) }}
          </div>
          <div class="align-center flex flex-shrink-0 justify-end">
            <button
              v-if="showRefreshButton"
              :disabled="isFetching"
              data-testid="refreshItemsButton"
              class="btn btn-white hidden md:flex"
              @click="fetchItems()"
            >
              <fdx-icon-loader v-if="isFetching" class="size-6 stroke-primary" />
              <fdx-icon-reload v-else class="size-6 text-black" />
            </button>
            <button
              v-if="hasFilters && isFilterInHeader"
              :data-testid="camelCase(`${dataTestIdPrefix}FilterButton`)"
              class="btn btn-white ms-2 inline-flex shrink-0 items-center px-8 py-2"
              @click="filtersModalOpened = true"
            >
              <fdx-icon-filter-icon class="size-4" />

              <span class="hidden md:ms-2 md:flex">
                {{ $t('button_label.filter') }}
              </span>
              <template v-if="activeFiltersCount()">
                <span
                  class="ms-2 flex size-6 shrink-0 items-center justify-center rounded-full bg-gray-500 text-xs text-white"
                >
                  {{ activeFiltersCount() }}
                </span>
                <span v-if="!showTabs" class="ms-2 hidden">
                  <button
                    class="flex"
                    data-testid="clearFilterButton"
                    @click.stop="clearAllFilters"
                  >
                    <fdx-icon-cross-in-circle class="size-6 text-gray-500" />
                  </button>
                </span>
              </template>
            </button>
            <div class="ms-2 flex gap-2">
              <slot name="headerRight"></slot>
            </div>
          </div>
        </div>
        <div class="card-body !mt-0">
          <div v-if="!displayItems.length" class="rounded-lg bg-white p-8 text-center font-light">
            <span v-if="filtersRequired && !hasFiltersBeenSelected">
              {{ $t('Please select some filters first.') }}
            </span>
            <screen-loader v-else-if="isFetching" />
            <span v-else class="error-message mx-auto block max-w-4xl">
              {{ $t(noRecordsText) }}
            </span>
          </div>

          <div v-if="displayItems.length">
            <div
              v-if="selectedItemsIds.length && !isBulkActionsDisabled"
              class="bulk-actions-bar my-2 flex items-center rounded-lg ps-4"
            >
              <span class="font-semibold">
                {{ `${selectedItemsIds.length} ${$t('listing_screen.selected')}` }}
              </span>

              <fdx-dropdown
                v-if="$slots.selectedItemsActions"
                class="ms-5"
                dropdown-content-classes="p-4"
              >
                <template #trigger>
                  <button type="button" class="btn btn-white flex items-center">
                    <span class="me-1">
                      {{ $t('actions') }}
                    </span>
                    <fdx-icon-arrow-down class="ms-2 size-2" />
                  </button>
                </template>
                <template #menu>
                  <slot name="selectedItemsActions" />
                </template>
              </fdx-dropdown>
            </div>

            <template v-if="!isFetching">
              <slot name="beforeTable" />
            </template>

            <fdx-scroll-fade v-if="displayItems.length">
              <div
                ref="tableContent"
                scroll-container-ref="listing-table"
                :class="[
                  'table-wrapper w-full rounded-b-lg',
                  {
                    'rounded-lg': tabs.length === 0 && !showRefreshButton && !hasFilters,
                  },
                  isCustomerPortal ? 'table-wrapper-cp' : 'table-wrapper-mms',
                ]"
              >
                <table id="listing-table" data-testid="listingTable" class="card-table w-full">
                  <thead :class="{ 'fixed-thead': fixedHeader }">
                    <tr
                      :class="{
                        'whitespace-nowrap': noWrap,
                      }"
                    >
                      <th v-if="!isBulkActionsDisabled" class="flex">
                        <checkbox :selected="allItemsSelected" @click="toggleAllItemsSelection" />
                      </th>
                      <template v-for="(column, i) in columns" :key="i">
                        <th
                          class="items-center space-x-1 px-4 py-2"
                          :class="{
                            'cursor-pointer': column.sortable,
                          }"
                          @click="column.sortable ? updateSortBy(column.name) : null"
                        >
                          <div class="flex items-center">
                            <span class="text-body font-bold">{{ $t(column.label) }}</span>

                            <span v-if="column.sortable" class="ml-1 flex items-center">
                              <fdx-icon-sort-up
                                v-if="sort.by === column.name"
                                :class="[
                                  'size-2',
                                  {
                                    'rotate-180': sort.direction === '-',
                                  },
                                ]"
                              />
                              <fdx-icon-unfold-more v-else class="size-4" />
                            </span>
                          </div>
                        </th>
                      </template>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(item, i) in displayItems"
                      :key="i"
                      :class="{
                        'cursor-pointer': isRowClickable,
                        'whitespace-nowrap': noWrap,
                      }"
                      @click.exact="handleRowClick(item)"
                      @click.ctrl="handleRowClick(item, true)"
                      @click.meta="handleRowClick(item, true)"
                    >
                      <td
                        v-if="!isBulkActionsDisabled && !excludeFromSelectionCallback(item)"
                        class="flex"
                        @click.stop
                      >
                        <checkbox
                          :selected="itemIsSelected(item)"
                          @click="toggleItemSelection(item)"
                        />
                      </td>
                      <td
                        v-else-if="!isBulkActionsDisabled && excludeFromSelectionCallback(item)"
                      ></td>
                      <slot name="tableRow" :item>
                        <td v-for="key in Object.keys(item)" :key>
                          {{ item[key] }}
                        </td>
                      </slot>
                    </tr>
                  </tbody>
                  <tfoot v-if="$slots.tableFooter">
                    <tr class="table-footer border-t border-gray-300">
                      <slot name="tableFooter" :items="displayItems" :response-details> </slot>
                    </tr>
                  </tfoot>
                </table>
                <div class="infinite-scroll-trigger">
                  <Fdx-intersection-trigger
                    v-if="infiniteScroll && !isFetching && items.length >= (perPage || 50)"
                    @intersected="nextPage"
                  />
                </div>
              </div>
            </fdx-scroll-fade>
          </div>
          <div
            v-if="hasPagination && displayItems.length && !infiniteScroll"
            id="table_pagination"
            data-testid="tablePagination"
          >
            <div
              class="rounded-b border-t border-gray-300 pt-4 text-footnote text-gray-800 md:flex"
              :class="[
                responseMeta.total || responseMeta.from || responseMeta.to
                  ? 'md:justify-between'
                  : 'md:justify-end',
              ]"
            >
              <div v-if="canShowPaginationInfo">
                {{
                  `${$t('listing_screen.showing')} ${
                    responseMeta.from
                  } ${$t('listing_screen.to')} ${responseMeta.to} ${$t('listing_screen.out_of')} ${
                    responseMeta.total
                  }`
                }}
              </div>
              <div v-else-if="isCursor && responseMeta.total">
                {{
                  `${$t('listing_screen.total')} ${roundDownToNearestThousand(responseMeta.total)}`
                }}
              </div>
              <div
                v-if="perPage === -1 || !(currentPage === 1 && items.length < perPage)"
                class="flex justify-between"
                :class="
                  canShowPaginationInfo || isCursor
                    ? ''
                    : 'flex-col md:flex md:w-full md:justify-between'
                "
              >
                <button
                  class="me-2 font-bold text-primary"
                  :disabled="currentPage == 1 || (isCursor && !responseMeta.prev_cursor)"
                  @click="previousPage"
                >
                  {{ $t('button_label.previous') }}
                </button>
                <button
                  class="font-bold text-primary"
                  :disabled="
                    items.length === 0 ||
                    (perPage !== -1 && responseMeta.last_page <= currentPage) ||
                    (isCursor && !responseMeta.next_cursor)
                  "
                  @click="nextPage"
                >
                  {{ $t('button_label.next') }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style>
.bulk-actions-bar {
  height: 56px;
  z-index: 20;
}

.rtl .bulk-actions-bar {
  right: 54px;
}

.ltr .bulk-actions-bar {
  left: 54px;
}

.rtl .card-header a {
  /* This is to fix the arabic font that gets clipped in listing screen */
  display: inline-block;
}

.card-header a {
  padding-bottom: 0;
}

.fixed-table thead,
.fixed-table tfoot,
.fixed-first-col tr td:first-child,
.fixed-first-col th:first-child,
.fixed-first-col.has-bulk-actions tr td:nth-child(2),
.fixed-first-col.has-bulk-actions th:nth-child(2) {
  position: sticky;
  background-color: rgb(247, 250, 252);
}
.fixed-table thead.fixed-thead {
  top: 0;
  z-index: 10;
}
.fixed-first-col tr:hover td:first-child,
.fixed-first-col tr:hover th:first-child,
.fixed-first-col tr:hover td:nth-child(2),
.fixed-first-col tr:hover th:nth-child(2) {
  background-color: rgb(247, 250, 252) !important;
}
.fixed-table tfoot {
  bottom: 0;
  z-index: 10;
}
/* should be refactored with the accurate numbers  */
.fixed-table .table-wrapper-cp {
  max-height: calc(100vh - 24rem);
}

.fixed-table .table-wrapper-mms {
  max-height: calc(100vh - 21rem);
}

#listing-table thead tr {
  position: sticky;
  top: 0;
  z-index: 1;
}

.tabs-container {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
.tabs-container::-webkit-scrollbar {
  display: none;
}

.infinite-scroll-trigger {
  position: relative;
  height: 1px;
  margin-top: -1px;
}
</style>
